import React, { useEffect, useState } from "react";

import { AlertV4, ModalV4 } from "./ui";

/* ============================================================== */
/*                                                                */
/*   CommunicationNoteModalV4  ·  pop-up Communication Note       */
/*   ของ encounter หนึ่ง (#86727 / #86729)                         */
/*                                                                */
/*   presentational ล้วน ๆ — ไม่รู้จัก sequence ใด                 */
/*   หน้าใดเรียกใช้ได้ทั้ง Patient Queue (MainHISV4) และหน้าอื่น  */
/*   ขอแค่: encounter (มี medical_record = EMR), notes, และ      */
/*   callback onSave/onClose จาก caller                          */
/*                                                                */
/*   Communication Note = DPO sensitive_note ต่อ EMR             */
/*   (endpoint /apis/DPO/sensitive_note/emr/{emr}/ — same stack  */
/*   กับ LOAD/SAVE_SENSITIVE_NOTE ของหน้าตรวจรักษา ExamV4)       */
/*                                                                */
/*   Layout ตาม design mock docs/design/next-pages/              */
/*   PatientQueue.dc.html: กล่อง 520px · list note พื้น #E8F3F7  */
/*   · ทางลัดข้อความ · input + ปุ่มบันทึกพื้นฟ้า #1B7FCC         */
/*                                                                */
/* ============================================================== */

/** ข้อมูลขั้นต่ำของ encounter ที่ modal ต้องแสดง (QueueEncounter ใช้ได้ตรง shape) */
export type CommunicationNoteEncounter = {
  patient_name: string;
  hn: string;
  number: string;
  /** id ของเวชระเบียน (EMR) —ขาดไม่ได้ในการบันทึก note */
  medical_record?: number;
};

/** รูปทรงของ Note 1 record ที่ modal ต้องแสดง (CommunicationNoteInfo ตรง shape นี้พอดี) */
export type CommunicationNoteView = {
  id?: number;
  note: string;
  editUser: string;
  editedAt: string;
  createdAt?: string;
};

export type CommunicationNoteModalV4Props = {
  encounter: CommunicationNoteEncounter;
  /** Note ทั้งหมดของ encounter นี้ (จาก notesByEmr ของ caller) — record ว่างจะถูกกรองออก */
  notes?: CommunicationNoteView[];
  /** ข้อผิดพลาดล่าสุดจากการบันทึก (แสดงเป็นแถบ danger) */
  error?: string | null;
  /** กำลังบันทึก (ปุ่ม disabled + ข้อความ "กำลังบันทึก…") */
  saving?: boolean;
  onClose: () => void;
  /** ผู้กด "บันทึก" — text ใหม่ที่จะเพิ่มเข้า encounter (parent ส่งเข้า sequence เอง) */
  onSave: (note: string) => void;
};

/** ทางลัดข้อความยอดฮิตตาม design mock — กดแล้วเติมลงช่องข้อความทันที */
export const COMMUNICATION_NOTE_QUICK_TEXTS = [
  "ชำระเงินแล้ว",
  "เซ็น Consent แล้ว",
  "ชำระเงินแล้ว และเซ็น Consent แล้ว",
];

const CommunicationNoteModalV4 = (props: CommunicationNoteModalV4Props): React.ReactElement => {
  const [draft, setDraft] = useState("");
  const emrId = props.encounter.medical_record;

  /* เปิดแถวใหม่ (ต่าง encounter/ต่าง EMR) -> ล้างร่าง note ของเก่า */
  useEffect(() => {
    setDraft("");
  }, [props.encounter.medical_record]);

  const notes = (props.notes ?? []).filter((note) => note.note !== "");
  const canSave = emrId !== undefined && draft.trim() !== "" && !props.saving;

  const handleSave = (): void => {
    if (!canSave) return;
    const text = draft.trim();
    setDraft("");
    props.onSave(text);
  };

  return (
    /* width 520 = ตาม mock (hv4-modal-card รับ max-width จาก prop ของ ModalV4) */
    <ModalV4 title="Communication Note" width={520} onClose={props.onClose}>
      <p className="hisv4-note-modal-sub">
        {`${props.encounter.patient_name} · HN ${props.encounter.hn} · Encounter ${props.encounter.number}`}
      </p>
      {props.error ? <AlertV4 tone="danger" error={props.error} /> : null}
      {notes.length > 0 ? (
        /* ข้อ 3 ของ #86727: แสดง Note ทั้งหมดของ encounter นี้ */
        <div className="hisv4-note-list">
          {notes.map((note, index) => (
            <div className="hisv4-note-item" key={note.id ?? index}>
              <div className="hisv4-note-text">{note.note}</div>
              <div className="hisv4-note-meta">
                {[note.editUser, note.editedAt].filter((v) => v !== "").join(" · ")}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="hv4-placeholder">
          {emrId === undefined
            ? `ไม่พบเวชระเบียน (EMR) ของ encounter ${props.encounter.number} จึงแสดง Communication Note ไม่ได้`
            : "ยังไม่มี Note ใน Encounter นี้"}
        </div>
      )}
      {emrId !== undefined && (
        /* ข้อ 3 ของ #86727: ช่องสำหรับเพิ่ม text note ใหม่ (จำนวนบนแถวอัปเดต
           ทันทีหลัง save — caller เรียก SAVE_NOTE แล้ว reload notes เองในฝั่ง sequence) */
        <div className="hisv4-note-form">
          <div className="hisv4-note-quick">
            {COMMUNICATION_NOTE_QUICK_TEXTS.map((t) => (
              <button
                type="button"
                key={t}
                className="hisv4-note-quick-chip"
                onClick={() => setDraft(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="hisv4-note-row">
            <input
              className="hisv4-note-input"
              aria-label="ข้อความ Note"
              placeholder="พิมพ์ข้อความถึงทีม (พยาบาล / แพทย์)"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              /* #86728 ข้อ 5: กด Enter เพื่อบันทึกได้ (Shift+Enter = ขึ้นบรรทัดใหม่ในค่าเดิมได้) */
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                }
              }}
            />
            <button type="button" className="hisv4-note-save" disabled={!canSave} onClick={handleSave}>
              {props.saving ? "กำลังบันทึก…" : "บันทึก"}
            </button>
          </div>
        </div>
      )}
    </ModalV4>
  );
};

export default CommunicationNoteModalV4;
